import React, { useState } from 'react';
import Calendar from 'react-calendar';
import Select from 'react-select';
import * as dayjs from 'dayjs';
import Modal from './modal';
import { useTranslation } from 'react-i18next';
import { createPractice } from '../../services/practices';

function DateDialog({ close, onDateTimeChange, initialDateTime, teamId, practice, method, onPracticeCreated }) {
  const [show, setShow] = useState(true);
  const { date, hour, minute } = initialDateTime;
  const [dateState, setDateState] = useState(date || new Date());
  const [selectedHour, setSelectedHour] = useState(method === 'clone' ? dayjs(practice?.scheduled_at).hour().toString().padStart(2, '0') : hour);
  const [selectedMinute, setSelectedMinute] = useState(method === 'clone' ? dayjs(practice?.scheduled_at).minute().toString().padStart(2, '0') : minute);
  const { t, i18n } = useTranslation();

  const closeWindow = () => {close ? close() : setShow(false)};

  const hoursOptions = Array.from({ length: 24 }, (_, i) => ({
    value: String(i).padStart(2, '0'),
    label: String(i).padStart(2, '0'),
  }));

  const minutesOptions = Array.from({ length: 60 }, (_, i) => ({
    value: String(i).padStart(2, '0'),
    label: String(i).padStart(2, '0'),
  }));

  const dateTimeSelect = () => {
    if (close) {
      close();
    }
    onDateTimeChange(dateState, selectedHour, selectedMinute);
  };

  const cancel = () => {
    closeWindow();
  };

  const create = async () => {
    const formattedDate = dayjs(dateState)
      .set('hour', selectedHour)
      .set('minute', selectedMinute)
      .format('YYYY-MM-DDTHH:mm:ss');

    const practiceData = {
      scheduled_at: formattedDate,
      source_method: method,
      team_id: teamId,
      ...(method === 'from_template' && { practice_template_id: practice.id }),
      ...(method === 'clone' && { source_practice_id: practice.id }),
    };

    try {
      await createPractice(practiceData);
      onPracticeCreated();
      closeWindow();
    } catch (error) {
      console.error('Error creating practice:', error);
    }
  };

  const dialogTitles = {
    from_template: t('dialog.create_from_template'),
    generate: t('dialog.create_suggested'),
    default: t('dialog.select_data'),
    clone: t('dialog.clone_practice'),
  };

  const title = dialogTitles[method] || dialogTitles.default;

  const tileDisabled = ({ date }) => dayjs(date).isBefore(dayjs().startOf('day'));

  return (
    <Modal openModal={show} closeModal={closeWindow}>
      <div className="data-dialog">
        <div className="dialog-header flex1 flex-column">
          <div className="dialog-header-title">{title}</div>
          {practice?.title && <div className="dialog-header-name">{t('teams.template', { title: practice.title })}</div>}
        </div>
        <div className="flex1 flex-between">
          <div className="calendar-section">
            <div className="date-display">{dayjs(dateState).format('dddd, D MMMM YYYY')}</div>
            <div className="calendar-editor-scene">
              <Calendar
                value={dateState}
                locale={i18n.language}
                onChange={date => setDateState(date)}
                formatDay={(_, date) => dayjs(date).format('D')}
                formatMonthYear={(_, date) => dayjs(date).format('MMM YYYY')}
                tileDisabled={tileDisabled}
              />
            </div>
          </div>
          <div className="time-picker-element">
            <div>
              <div className="selected-time">{t('teams.selected_time', { hour: selectedHour, minute: selectedMinute })}</div>
              <div className="select-wrapper">
                <div className="select-wrapper-filters">
                  <Select
                    options={hoursOptions}
                    onChange={selectedOption => setSelectedHour(selectedOption.value)}
                    value={hoursOptions.find(option => option.value === selectedHour) || null}
                    styles={customStyles}
                  />
                </div>
                <div className="select-wrapper-filters">
                  <Select
                    options={minutesOptions}
                    onChange={selectedOption => setSelectedMinute(selectedOption.value)}
                    value={minutesOptions.find(option => option.value === selectedMinute) || null}
                    styles={customStyles}
                  />
                </div>
              </div>
            </div>
            <div className="select-buttons">
              <button className="button" onClick={cancel}>{t('buttons.common.cancel')}</button>
              {!practice ? (
                <button className="button button-primary" onClick={dateTimeSelect}>{t('buttons.common.save')}</button>
              ) : (
                <button className="button button-primary" onClick={create}>{t('buttons.common.save')}</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default DateDialog;

const customStyles = {
  control: provided => ({
    ...provided,
    backgroundColor: '#F7F7F7',
    boxShadow: 'none',
    border: 'none',
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isFocused ? '#29304b' : '#7a7a7a',
  }),
  option: (provided, { isFocused, isSelected }) => ({
    ...provided,
    backgroundColor: isSelected ? '#29304b' : isFocused ? '#E0E0E0' : '#FFFFFF',
    color: isSelected ? '#FFFFFF' : '#7a7a7a',
  }),
  multiValue: styles => ({
    ...styles,
    backgroundColor: 'none',
  }),
};
